/**
 * Where the Simkl token lives.
 *
 * The token is the whole prize: multi-year, non-refreshing, and able to rewrite
 * the user's entire watch history. It stays on the device (KTD1) with a strict
 * CSP as the compensating control (KTD2), and is never sent anywhere except
 * api.simkl.com.
 *
 * IndexedDB rather than localStorage: not because it resists XSS any better —
 * it does not — but because it is async, survives better on iOS, and shares the
 * database with the cache so signing out is one wipe.
 */
import { META_STORE, withStore, wipe } from "@/store/db";

const TOKEN_KEY = "simkl-token";

export interface Vault {
  read: () => Promise<string | null>;
  write: (token: string) => Promise<void>;
  clear: () => Promise<void>;
}

export const indexedDbVault: Vault = {
  async read() {
    const [value] = await withStore<unknown>(META_STORE, "readonly", (store) =>
      store.get(TOKEN_KEY),
    );
    return typeof value === "string" && value.length > 0 ? value : null;
  },
  async write(token) {
    await withStore(META_STORE, "readwrite", (store) => store.put(token, TOKEN_KEY));
  },
  async clear() {
    // Everything, not just the token: signing out should leave no watch history
    // on the device either.
    await wipe();
  },
};

/**
 * Cache of the token for synchronous reads.
 *
 * The API client needs the token synchronously on every request, and IndexedDB
 * is async. The vault is read once at startup into this holder; nothing else
 * touches the database on the request path.
 */
export class TokenHolder {
  #token: string | null = null;

  constructor(private readonly vault: Vault) {}

  /** Loads the persisted token. Call once, before the first request. */
  async load(): Promise<string | null> {
    this.#token = await this.vault.read();
    return this.#token;
  }

  get(): string | null {
    return this.#token;
  }

  async set(token: string): Promise<void> {
    this.#token = token;
    await this.vault.write(token);
  }

  /** Forgets the token in memory and wipes all local data. */
  async clear(): Promise<void> {
    this.#token = null;
    await this.vault.clear();
  }
}
